import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PublicLayout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { api, money, pct, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDisplayCurrency } from "@/context/CurrencyContext";
import { MapPin, ArrowRight, ShieldCheck, RefreshCcw } from "lucide-react";

export function SecondaryMarket() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { currency: displayCur, convert } = useDisplayCurrency();
  useEffect(() => { api.get("/secondary/listings").then((r) => setItems(r.data)); }, []);
  const buy = async (id) => {
    if (!user || !user.user_id) { toast.error("Please sign in to buy"); navigate("/login"); return; }
    if (user.kyc_status !== "approved") { toast.error("Complete KYC before buying"); navigate("/kyc"); return; }
    setLoading(id);
    try {
      const { data } = await api.post(`/secondary/listings/${id}/buy`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setLoading(null); }
  };
  return (
    <PublicLayout>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="overline">Secondary Market</div>
        <h1 className="font-serif text-5xl md:text-6xl mt-3 text-[#0B192C]">Investor-to-investor units.</h1>
        <p className="mt-4 text-slate-600 max-w-2xl leading-relaxed">
          After the 12-month lockup, investors can list their fractions for sale. Every trade settles instantly on-platform with escrow protection.
        </p>
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {items.map((l) => (
            <div key={l.id} className="bg-white border border-[#E5DFD5] rounded-2xl overflow-hidden hover-lift" data-testid={`listing-${l.id}`}>
              <div className="relative aspect-[4/3] bg-[#F3EFEA]">
                <img src={l.hero_image} alt="" className="w-full h-full object-cover" />
                <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[10px] font-semibold tracking-widest uppercase bg-[#0F5132] text-white">
                  Secondary
                </span>
              </div>
              <div className="p-5 space-y-3">
                <div>
                  <h3 className="text-lg font-serif font-medium text-[#0B192C]">{l.property_title}</h3>
                  <div className="text-sm text-slate-500 flex items-center gap-1 mt-1"><MapPin className="h-3.5 w-3.5" />{l.location}</div>
                </div>
                <div className="flex justify-between items-baseline">
                  <div><div className="overline">Yield</div><div className="font-mono text-lg text-[#0F5132] font-semibold">{pct(l.assured_rental_yield, 1)}</div></div>
                  <div className="text-right"><div className="overline">Units</div><div className="font-mono text-lg text-[#0B192C]">{l.units}</div></div>
                  <div className="text-right">
                    <div className="overline">Total</div>
                    <div className="font-mono text-lg text-[#0B192C]">{money(convert(l.total_price, l.currency), displayCur)}</div>
                    {(l.currency || "INR").toUpperCase() !== displayCur && (
                      <div className="text-[10px] font-mono text-slate-400">≈ {money(l.total_price, l.currency)}</div>
                    )}
                  </div>
                </div>
                <Button className="w-full btn-gold rounded-full mt-2" onClick={() => buy(l.id)} disabled={loading === l.id} data-testid={`buy-${l.id}`}>
                  {loading === l.id ? "Redirecting…" : "Buy Now"}<ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          ))}
        </div>
        {items.length === 0 && (
          <div className="mt-10 text-center py-20 bg-white border border-dashed border-[#E5DFD5] rounded-2xl">
            <RefreshCcw className="h-8 w-8 mx-auto text-slate-400" />
            <p className="mt-4 text-slate-500">No active listings right now. Check back soon.</p>
          </div>
        )}
      </div>
    </PublicLayout>
  );
}

export function ListForSaleDialog({ holding, onListed }) {
  const [open, setOpen] = useState(false);
  const [units, setUnits] = useState(1);
  const [pricePer, setPricePer] = useState(1000);
  const [loading, setLoading] = useState(false);
  const available = holding.available_to_list || 0;
  const currency = holding.currency || "INR";
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/secondary/listings", {
        investment_id: holding.investment_id,
        units: parseInt(units),
        price_per_unit: parseFloat(pricePer),
      });
      toast.success("Listed on secondary market");
      setOpen(false); onListed && onListed();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const disabled = !holding.is_unlocked || available === 0;
  const buttonLabel = holding.listed_units > 0 ? "Already listed" : "List for sale";
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" disabled={disabled} data-testid={`list-${holding.investment_id}`}>
          {buttonLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>List units for sale</DialogTitle></DialogHeader>
        <div className="text-sm text-slate-600 mb-3">
          <div className="font-medium text-[#0B192C]">{holding.property_title}</div>
          <div className="mt-1">Available to list: <span className="font-mono">{available}</span> units</div>
        </div>
        <form onSubmit={submit} className="space-y-5">
          <div>
            <div className="flex justify-between mb-2"><Label>Units to sell</Label><span className="font-mono">{units}</span></div>
            <Slider value={[units]} min={1} max={Math.max(1, available)} step={1} onValueChange={(v) => setUnits(v[0])} data-testid="list-units-slider" />
          </div>
          <div>
            <Label>Price per unit ({currency})</Label>
            <Input type="number" step="0.01" min="1" value={pricePer} onChange={(e) => setPricePer(e.target.value)} required data-testid="list-price" />
          </div>
          <div className="bg-[#F3EFEA] rounded-lg p-4 text-sm">
            <div className="flex justify-between"><span className="text-slate-600">Total listing price</span><span className="font-mono text-[#0B192C] font-semibold">{money(units * (parseFloat(pricePer) || 0), currency)}</span></div>
            <div className="flex justify-between mt-1 text-xs text-slate-500"><span>Settled to your wallet on sale</span></div>
          </div>
          <Button type="submit" className="w-full btn-gold rounded-full" disabled={loading} data-testid="list-submit">
            {loading ? "Listing…" : "Confirm listing"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
